// components/Seo.js
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_CONFIG } from '../pages/PAGE_SEO';

const Seo = ({ 
  title, 
  description, 
  keywords, 
  image, 
  url, 
  type = "website",
  author,
  publishedTime,
  modifiedTime,
  articleTags,
  noIndex = false
}) => {
  // Use site defaults if not provided
  const metaTitle = title ? `${title} | ${SITE_CONFIG.name}` : SITE_CONFIG.title;
  const metaDesc = description || SITE_CONFIG.description;
  const metaKeywords = keywords || SITE_CONFIG.keywords;
  const metaImage = image || SITE_CONFIG.image;
  const metaUrl = url || (typeof window !== 'undefined' ? window.location.href : SITE_CONFIG.url);
  const metaAuthor = author || SITE_CONFIG.author;

  // Format keywords if array or string
  const formatKeywords = () => {
    if (Array.isArray(metaKeywords)) return metaKeywords.join(', ');
    return metaKeywords;
  };

  // JSON-LD Structured Data (Organization)
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": SITE_CONFIG.name,
    "url": SITE_CONFIG.url,
    "logo": SITE_CONFIG.organization.logo,
    "sameAs": SITE_CONFIG.organization.sameAs,
    "description": SITE_CONFIG.description
  };

  // JSON-LD for WebSite
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": SITE_CONFIG.name,
    "url": SITE_CONFIG.url,
    "description": SITE_CONFIG.description,
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${SITE_CONFIG.url}/search?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };

  // JSON-LD for BlogPosting (if type is article)
  const articleSchema = type === "article" ? {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "description": metaDesc,
    "image": metaImage,
    "author": {
      "@type": "Person",
      "name": metaAuthor
    },
    "datePublished": publishedTime,
    "dateModified": modifiedTime || publishedTime,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": metaUrl
    },
    "keywords": formatKeywords(),
    "articleSection": articleTags ? articleTags.join(', ') : undefined
  } : null;

  // Combine schemas
  const schemas = [organizationSchema, websiteSchema];
  if (articleSchema) schemas.push(articleSchema);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{metaTitle}</title>
      <meta name="description" content={metaDesc} />
      <meta name="keywords" content={formatKeywords()} />
      <meta name="author" content={metaAuthor} />
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      )}
      
      {/* Canonical URL */}
      <link rel="canonical" href={metaUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:url" content={metaUrl} />
      <meta property="og:site_name" content={SITE_CONFIG.name} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDesc} />
      <meta name="twitter:image" content={metaImage} />
      <meta name="twitter:site" content={SITE_CONFIG.twitterHandle} />
      <meta name="twitter:creator" content={metaAuthor} />
      
      {/* Article Specific (for blog posts) */}
      {type === "article" && (
        <>
          <meta property="article:published_time" content={publishedTime} />
          <meta property="article:modified_time" content={modifiedTime || publishedTime} />
          <meta property="article:author" content={metaAuthor} />
          {articleTags && articleTags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Additional SEO Meta */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="theme-color" content="#00F0FF" />
      
      {/* Language */}
      <meta httpEquiv="Content-Language" content="en" />
      
      {/* Geo (if applicable) */}
      <meta name="geo.region" content="US" />
      <meta name="geo.position" content="40.7128;-74.0060" />
      <meta name="ICBM" content="40.7128, -74.0060" />
      
      {/* Structured Data (JSON-LD) */}
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;